import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Cartao, Indicador, Selo, classesBotao } from '../../../web/src/componentes/base';
import { Estado, Vazio } from '../../../web/src/componentes/estados';
import { CodigoQr } from '../../../web/src/componentes/qr';
import { dinheiro, textoLivre } from '../../../web/src/nucleo/formato';
import { descricaoGerada } from '../../../web/src/nucleo/texto-sistema';
import { pget } from '../api';
import { useSessao } from '../sessao';
import { BotaoCopiar, competenciaCurta, dataCurta } from './comum';

/** Espelho de `FaturaPortal` (`modules/contas/src/publico.ts`). */
export interface Fatura {
  id: string;
  numero: string;
  descricao: string;
  emissao: string;
  vencimento: string;
  competencia: string | null;
  parcela: number;
  parcelas: number;
  valor_centavos: number;
  desconto_centavos: number;
  saldo_centavos: number;
  moeda: string;
  estado: 'em_aberto' | 'parcial' | 'pago';
  vencida: boolean;
  pago_em: string | null;
  cobranca: { forma: string | null; link: string | null; boleto_url: string | null; linha_digitavel: string | null; pix_copia_e_cola: string | null; pix_expira_em: string | null } | null;
}

interface Resumo {
  moeda: string;
  faturado: number;
  pago: number;
  aberto: number;
  vencido: number;
}

export function SeloEstado({ f }: { f: Pick<Fatura, 'estado' | 'vencida'> }) {
  const { t } = useTranslation('portal');
  if (f.estado === 'pago') return <Selo cor="verde">{t('financeiro.estado.pago')}</Selo>;
  if (f.vencida) return <Selo cor="perigo">{t('financeiro.estado.vencida')}</Selo>;
  return <Selo cor={f.estado === 'parcial' ? 'laranja' : 'neutro'}>{t(`financeiro.estado.${f.estado}`)}</Selo>;
}

function Linha({ f }: { f: Fatura }) {
  const { t, i18n } = useTranslation('portal');
  const idioma = i18n.language.slice(0, 2);
  return (
    <li>
      <Link to={`/financeiro/${f.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-sup-2" data-fatura={f.id}>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {f.numero}
            {f.parcelas > 1 ? ` · ${t('financeiro.parcela', { parcela: f.parcela, parcelas: f.parcelas })}` : ''}
          </p>
          <p className="truncate text-xs text-tinta-3">{f.estado === 'pago' && f.pago_em ? t('financeiro.pagoEm', { data: dataCurta(f.pago_em, idioma) }) : `${t('financeiro.vencimento')}: ${dataCurta(f.vencimento, idioma)}`}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="numero text-sm font-semibold">{dinheiro(f.estado === 'pago' ? f.valor_centavos - f.desconto_centavos : f.saldo_centavos, f.moeda)}</span>
          <SeloEstado f={f} />
        </div>
      </Link>
    </li>
  );
}

/** Espelho de `AssinaturaPortal` (`modules/assinaturas/src/publico.ts`). */
interface Assinatura {
  id: string;
  numero: string;
  estado: 'ativo' | 'pausado';
  tipo: 'recorrente' | 'avulso';
  intervalo_meses: number;
  unidade_recorrencia: 'dia' | 'semana' | 'mes';
  inicio: string;
  termino: string | null;
  moeda: string;
  itens: { descricao: string; quantidade: number }[];
  proximas: { vencimento: string; valor_centavos: number; moeda: string }[];
}

/**
 * Pacote 2 (onda 1a, spec `portal-cliente`: gestão da assinatura): as assinaturas ativas ou pausadas do cliente, com a
 * recorrência, a vigência, os itens e as próximas cobranças agendadas. Só leitura; mudar ou cancelar é com a equipe. Sem o
 * Assinaturas instalado, a seção não aparece.
 */
function Assinaturas() {
  const { t, i18n } = useTranslation('portal');
  const idioma = i18n.language.slice(0, 2);
  const s = useSessao();
  const q = useQuery({ queryKey: ['portal', 'assinaturas'], queryFn: () => pget<{ itens: Assinatura[]; instalado: boolean }>('/financeiro/assinaturas') });
  if (q.isSuccess && !q.data.instalado) return null;
  return (
    <Cartao titulo={t('financeiro.assinaturas.titulo')} semPadding>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length ? (
            <ul className="divide-y divide-borda">
              {d.itens.map((a) => (
                <li key={a.id} className="flex flex-col gap-2 px-4 py-3" data-assinatura={a.id}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="min-w-0 text-sm font-medium break-words">{a.itens.map((i) => textoLivre(i.descricao, s.empresa.nome)).join(', ') || a.numero}</p>
                    <Selo cor={a.estado === 'ativo' ? 'verde' : 'laranja'}>{a.estado === 'ativo' ? t('financeiro.assinaturas.ativa') : t('financeiro.assinaturas.pausada')}</Selo>
                  </div>
                  <p className="text-xs text-tinta-3">
                    {[
                      a.numero,
                      a.tipo === 'avulso' ? t('financeiro.assinaturas.avulso') : t(`financeiro.assinaturas.recorrencia.${a.unidade_recorrencia}`, { count: a.intervalo_meses }),
                      t('financeiro.assinaturas.desde', { data: dataCurta(a.inicio, idioma) }),
                      a.termino ? t('financeiro.assinaturas.ate', { data: dataCurta(a.termino, idioma) }) : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {a.proximas.length ? (
                    <div className="flex min-w-0 flex-col gap-1">
                      <p className="kicker">{t('financeiro.assinaturas.proximas')}</p>
                      <ul className="flex flex-col gap-1 text-sm">
                        {a.proximas.map((p) => (
                          <li key={p.vencimento} className="flex justify-between gap-3">
                            <span>{dataCurta(p.vencimento, idioma)}</span>
                            <span className="numero font-semibold">{dinheiro(p.valor_centavos, p.moeda)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-xs text-tinta-3">{t('financeiro.assinaturas.semProximas')}</p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="p-4 text-sm text-tinta-3">{t('financeiro.assinaturas.vazio')}</p>
          )
        }
      </Estado>
      {!!q.data?.itens.length && <p className="border-t border-borda px-4 py-3 text-xs text-tinta-3">{t('financeiro.assinaturas.ajuda')}</p>}
    </Cartao>
  );
}

/** Financeiro: faturas em aberto e pagas (12 meses), com o resumo do ano. "Pago" aparece sozinho pelo tempo real. */
export function Financeiro() {
  const { t } = useTranslation('portal');
  const q = useQuery({ queryKey: ['portal', 'faturas'], queryFn: () => pget<{ faturas: Fatura[]; resumo: Resumo[]; ano: number }>('/financeiro/faturas') });
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight">{t('financeiro.titulo')}</h1>
      <Estado consulta={q}>
        {(d) => {
          const abertas = d.faturas.filter((f) => f.estado !== 'pago');
          const pagas = d.faturas.filter((f) => f.estado === 'pago');
          return (
            <>
              {d.resumo.map((r) => (
                <section key={r.moeda} aria-label={t('financeiro.resumo', { ano: d.ano })} className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <Indicador rotulo={t('financeiro.aberto')} valor={dinheiro(r.aberto, r.moeda)} />
                  <Indicador rotulo={t('financeiro.vencido')} valor={dinheiro(r.vencido, r.moeda)} cor={r.vencido ? 'perigo' : 'tinta-1'} />
                  <Indicador rotulo={t('financeiro.faturado')} valor={dinheiro(r.faturado, r.moeda)} />
                  <Indicador rotulo={t('financeiro.pago')} valor={dinheiro(r.pago, r.moeda)} cor="verde" />
                </section>
              ))}
              <Cartao titulo={t('financeiro.abertas')} semPadding>
                {abertas.length ? <ul className="divide-y divide-borda">{abertas.map((f) => <Linha key={f.id} f={f} />)}</ul> : <p className="p-4 text-sm text-tinta-3">{t('financeiro.semAbertas')}</p>}
              </Cartao>
              <Cartao titulo={t('financeiro.pagas')} semPadding>
                {pagas.length ? <ul className="divide-y divide-borda">{pagas.map((f) => <Linha key={f.id} f={f} />)}</ul> : <p className="p-4 text-sm text-tinta-3">{t('financeiro.semPagas')}</p>}
              </Cartao>
              <Assinaturas />
              <p className="text-xs text-tinta-3">{t('financeiro.ateAgora')}</p>
            </>
          );
        }}
      </Estado>
    </div>
  );
}

function temaAtual(): 'escuro' | 'claro' {
  return document.documentElement.dataset.tema === 'claro' ? 'claro' : 'escuro';
}

/** Uma fatura: Pix copia e cola com o QR, linha digitável e boleto, e a fatura do Asaas. */
export function FaturaDetalhe() {
  const { t, i18n } = useTranslation('portal');
  const { id } = useParams<{ id: string }>();
  // Review r52 (VIS-M2): o link da fatura era de um cliente que esta conta não acessa: aviso claro, sem pedir a fatura.
  const outraEmpresa = !!(useLocation().state as { clienteNaoEncontrado?: boolean } | null)?.clienteNaoEncontrado;
  const q = useQuery({ queryKey: ['portal', 'fatura', id], queryFn: () => pget<Fatura>(`/financeiro/faturas/${id}`), enabled: !!id && !outraEmpresa });
  const idioma = i18n.language.slice(0, 2);
  const s = useSessao();
  const [pagoAgora, setPagoAgora] = useState(false);
  const anterior = useRef<string | null>(null);
  useEffect(() => {
    const estado = q.data?.estado ?? null;
    if (anterior.current && anterior.current !== 'pago' && estado === 'pago') setPagoAgora(true);
    anterior.current = estado;
  }, [q.data?.estado]);
  return (
    <div className="flex flex-col gap-4">
      <Link to="/financeiro" className="flex w-fit items-center gap-1 text-sm text-tinta-2 hover:text-acento-forte">
        <ArrowLeft className="size-4" aria-hidden />
        {t('financeiro.voltar')}
      </Link>
      {outraEmpresa ? (
        <Vazio titulo={t('financeiro.outraEmpresaTitulo')} texto={t('financeiro.outraEmpresa')} />
      ) : (
        <Estado consulta={q}>
          {(f) => (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">{f.numero}</h1>
                <SeloEstado f={f} />
              </div>
              {pagoAgora && (
                <p role="status" className="rounded-lg border border-verde px-3 py-2 text-sm text-verde">
                  {t('financeiro.pagoAgora')}
                </p>
              )}
              {/* O que paga vem primeiro (Pix e boleto); os detalhes da fatura ficam por último. */}
              <Cartao className="order-last">
                <dl className="grid gap-3 text-sm sm:grid-cols-2">
                  {f.descricao && (
                    <div className="sm:col-span-2">
                      <dd className="text-tinta-1 whitespace-pre-line">{textoLivre(descricaoGerada(f.descricao), s.empresa.nome)}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="kicker">{t('financeiro.valor')}</dt>
                    <dd className="numero text-lg font-semibold">{dinheiro(f.valor_centavos - f.desconto_centavos, f.moeda)}</dd>
                  </div>
                  {f.estado !== 'pago' && (
                    <div>
                      <dt className="kicker">{t('financeiro.saldo')}</dt>
                      <dd className="numero text-lg font-semibold">{dinheiro(f.saldo_centavos, f.moeda)}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="kicker">{t('financeiro.vencimento')}</dt>
                    <dd className={f.vencida ? 'text-perigo' : ''}>{dataCurta(f.vencimento, idioma)}</dd>
                  </div>
                  <div>
                    <dt className="kicker">{t('financeiro.emissao')}</dt>
                    <dd>{dataCurta(f.emissao, idioma)}</dd>
                  </div>
                  {f.competencia && (
                    <div>
                      <dt className="kicker">{t('financeiro.competencia')}</dt>
                      <dd>{competenciaCurta(f.competencia, idioma)}</dd>
                    </div>
                  )}
                  {f.pago_em && (
                    <div>
                      <dt className="kicker">{t('financeiro.estado.pago')}</dt>
                      <dd>{dataCurta(f.pago_em, idioma)}</dd>
                    </div>
                  )}
                </dl>
              </Cartao>
              {f.estado !== 'pago' &&
                (f.cobranca ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {f.cobranca.pix_copia_e_cola && (
                      <Cartao titulo={t('financeiro.pix')}>
                        <div className="flex flex-col items-center gap-3">
                          <CodigoQr texto={f.cobranca.pix_copia_e_cola} rotulo={t('financeiro.qr')} tema={temaAtual()} />
                          <p className="numero w-full rounded-lg border border-borda bg-sup-2 p-2 text-xs break-all text-tinta-2" data-pix>
                            {f.cobranca.pix_copia_e_cola}
                          </p>
                          <BotaoCopiar texto={f.cobranca.pix_copia_e_cola} rotulo={t('financeiro.copiar')} copiado={t('financeiro.copiado')} />
                        </div>
                      </Cartao>
                    )}
                    {(f.cobranca.linha_digitavel || f.cobranca.boleto_url || f.cobranca.link) && (
                      <Cartao titulo={t('financeiro.boleto')}>
                        <div className="flex flex-col gap-3">
                          {f.cobranca.linha_digitavel && (
                            <>
                              <p className="numero rounded-lg border border-borda bg-sup-2 p-2 text-sm break-all">{f.cobranca.linha_digitavel}</p>
                              <BotaoCopiar texto={f.cobranca.linha_digitavel} rotulo={t('financeiro.copiarLinha')} copiado={t('financeiro.copiado')} />
                            </>
                          )}
                          {f.cobranca.boleto_url && (
                            <a href={f.cobranca.boleto_url} target="_blank" rel="noopener noreferrer" className={classesBotao('secundario')}>
                              <ExternalLink className="size-4" aria-hidden />
                              {t('financeiro.abrirBoleto')}
                            </a>
                          )}
                          {f.cobranca.link && (
                            <a href={f.cobranca.link} target="_blank" rel="noopener noreferrer" className={classesBotao('primario')}>
                              <ExternalLink className="size-4" aria-hidden />
                              {t('financeiro.abrirFatura')}
                            </a>
                          )}
                        </div>
                      </Cartao>
                    )}
                  </div>
                ) : (
                  <p className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm text-tinta-2">{t('financeiro.semCobranca')}</p>
                ))}
            </>
          )}
        </Estado>
      )}
    </div>
  );
}
